import { useState } from "react";
import { useEncryption } from "@/hooks/useEncryption";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import { 
  IconLock, 
  IconKey, 
  IconDownload, 
  IconCopy, 
  IconAlertCircle, 
  IconEye, 
  IconEyeOff, 
  IconCheck, 
  IconFileText 
} from "@tabler/icons-react";

interface E2EEncryptionFlowProps {
  onSuccess: () => void;
}

export function SetupScreen({ onSuccess }: E2EEncryptionFlowProps) {
  const { setupEncryption } = useEncryption();
  const { signOut } = useAuth();
  const { toast } = useToast();
  
  const [passphrase, setPassphrase] = useState("");
  const [confirmPassphrase, setConfirmPassphrase] = useState("");
  const [showPassphrase, setShowPassphrase] = useState(false);
  
  const [step, setStep] = useState<"create" | "recovery">("create");
  const [recoveryKey, setRecoveryKey] = useState("");
  
  const [hasSavedRecovery, setHasSavedRecovery] = useState(false);
  const [hasDownloadedPdf, setHasDownloadedPdf] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passphrase.length < 8) {
      toast({
        title: "Weak Passphrase",
        description: "Passphrase must be at least 8 characters long.",
        variant: "destructive"
      });
      return;
    }
    if (passphrase !== confirmPassphrase) {
      toast({
        title: "Passphrases match error",
        description: "The passphrases you entered do not match.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await setupEncryption(passphrase);
      setRecoveryKey(result.recoveryKey);
      setStep("recovery");
      toast({
        title: "Encryption keys generated",
        description: "Your master key and recovery key have been derived."
      });
    } catch (err) {
      toast({
        title: "Setup failed",
        description: err instanceof Error ? err.message : "Failed to initialize E2E keys.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyRecoveryKey = () => {
    navigator.clipboard.writeText(recoveryKey);
    toast({
      title: "Copied to clipboard",
      description: "Your recovery key has been copied."
    });
  };

  const handleDownloadPDF = async () => {
    try {
      const { default: jsPDF } = await import("jspdf");
      const doc = new jsPDF({
        unit: "pt",
        format: "letter",
      });
      
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Decorative border
      doc.setDrawColor(200, 200, 200);
      doc.rect(30, 30, pageWidth - 60, doc.internal.pageSize.getHeight() - 60);
      
      doc.setFont("times", "bold");
      doc.setFontSize(26);
      doc.text("BIRCH RECOVERY KEY", pageWidth / 2, 90, { align: "center" });
      
      doc.setFont("times", "bold");
      doc.setFontSize(11);
      doc.text("DO NOT LOSE THIS DOCUMENT", pageWidth / 2, 120, { align: "center" });
      
      doc.setFont("times", "normal");
      doc.setFontSize(12);
      doc.text("Birch utilizes End-to-End Encryption to keep your manuscripts private.", pageWidth / 2, 160, { align: "center" });
      doc.text("Because of this security model, we do not store your master password or keys on our servers.", pageWidth / 2, 180, { align: "center" });
      doc.text("If you lose your passphrase, this Recovery Key is the ONLY way to unlock your manuscripts.", pageWidth / 2, 200, { align: "center" });
      
      doc.setFont("times", "bold");
      doc.text("Birch support CANNOT reset your passphrase or recover your data.", pageWidth / 2, 230, { align: "center" });
      
      // Key Box
      doc.setFillColor(245, 245, 245);
      doc.rect(50, 260, pageWidth - 100, 60, "F");
      doc.setDrawColor(220, 220, 220);
      doc.rect(50, 260, pageWidth - 100, 60, "S");
      
      doc.setFont("courier", "bold");
      doc.setFontSize(18);
      doc.text(recoveryKey, pageWidth / 2, 296, { align: "center" });
      
      doc.setFont("times", "normal");
      doc.setFontSize(11);
      doc.text("Store this document in a safe physical location or an encrypted key vault.", pageWidth / 2, 350, { align: "center" });
      doc.text("Generated on " + new Date().toLocaleDateString(), pageWidth / 2, 380, { align: "center" });
      
      doc.save("birch-recovery-key.pdf");
      setHasDownloadedPdf(true);
      toast({
        title: "PDF Downloaded",
        description: "Keep the recovery document safe."
      });
    } catch (err) {
      console.error(err);
      toast({
        title: "PDF generation failed",
        description: "Could not generate recovery PDF.",
        variant: "destructive"
      });
    }
  };

  const handleFinishSetup = () => {
    if (!hasSavedRecovery && !hasDownloadedPdf) {
      toast({
        title: "Action required",
        description: "Please copy or download your recovery key to proceed.",
        variant: "destructive"
      });
      return;
    }
    onSuccess();
  };

  if (step === "recovery") {
    return (
      <div className="h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-lg border-border shadow-xl">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl flex items-center gap-2">
              <IconKey className="text-primary" size={24} />
              Save Your Recovery Key
            </CardTitle>
            <CardDescription>
              This recovery key is the only way to recover your data if you forget your passphrase.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <IconAlertCircle size={20} />
              <AlertTitle className="font-bold">Birch cannot recover your data</AlertTitle>
              <AlertDescription className="text-xs">
                We do not hold a copy of your encryption key. Write this key down, print it, or save it to a password manager. If lost, your manuscripts cannot be recovered.
              </AlertDescription>
            </Alert>
            
            <div className="p-4 bg-muted border rounded-md font-mono text-lg text-center font-bold tracking-wider select-all">
              {recoveryKey}
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2">
              <Button type="button" variant="outline" className="flex-1 gap-2" onClick={handleCopyRecoveryKey}>
                <IconCopy size={16} /> Copy Key
              </Button>
              <Button type="button" variant="outline" className="flex-1 gap-2" onClick={handleDownloadPDF}>
                <IconDownload size={16} /> Download PDF
              </Button>
            </div>
            
            <div className="flex items-start space-x-2 pt-2">
              <Checkbox 
                id="saved-check" 
                checked={hasSavedRecovery} 
                onCheckedChange={(checked) => setHasSavedRecovery(checked === true)} 
              />
              <label htmlFor="saved-check" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer">
                I have safely stored my passphrase and recovery key.
              </label>
            </div>
          </CardContent>
          <CardFooter className="flex gap-2">
            <Button type="button" variant="ghost" className="flex-1 text-muted-foreground" onClick={() => signOut()}>
              Sign Out
            </Button>
            <Button 
              type="button" 
              className="flex-1" 
              onClick={handleFinishSetup} 
              disabled={!hasSavedRecovery}
            >
              Finish Setup
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-screen bg-background flex items-center justify-center p-4">
      <form onSubmit={handleCreate} className="w-full max-w-md">
        <Card className="border-border shadow-xl">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl flex items-center gap-2">
              <IconLock className="text-primary" size={24} />
              Set Up End-to-End Encryption
            </CardTitle>
            <CardDescription>
              Create a secure passphrase to encrypt your writing locally before it syncs to the cloud.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="bg-muted border border-border">
              <IconAlertCircle className="text-muted-foreground" size={20} />
              <AlertTitle className="font-semibold">Privacy Promise</AlertTitle>
              <AlertDescription className="text-xs">
                Your passphrase is never sent to our servers. Only you can decrypt your manuscripts.
              </AlertDescription>
            </Alert>
            
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="passphrase">Choose Encryption Passphrase</Label>
                <button 
                  type="button" 
                  onClick={() => setShowPassphrase(!showPassphrase)}
                  className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                >
                  {showPassphrase ? (
                    <>
                      <IconEyeOff size={14} /> Hide
                    </>
                  ) : (
                    <>
                      <IconEye size={14} /> Show
                    </>
                  )}
                </button>
              </div>
              <Input
                id="passphrase"
                type={showPassphrase ? "text" : "password"}
                value={passphrase}
                onChange={(e) => setPassphrase(e.target.value)}
                placeholder="Min. 8 characters"
                required
                disabled={isSubmitting}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassphrase">Confirm Passphrase</Label>
              <Input
                id="confirmPassphrase"
                type={showPassphrase ? "text" : "password"}
                value={confirmPassphrase}
                onChange={(e) => setConfirmPassphrase(e.target.value)}
                placeholder="Re-enter passphrase"
                required
                disabled={isSubmitting}
              />
            </div>
          </CardContent>
          <CardFooter className="flex gap-2">
            <Button type="button" variant="ghost" className="flex-1 text-muted-foreground" onClick={() => signOut()}>
              Sign Out
            </Button>
            <Button type="submit" className="flex-1" disabled={isSubmitting}>
              {isSubmitting ? "Generating Keys..." : "Next Step"}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
}

export function UnlockScreen({ onSuccess }: E2EEncryptionFlowProps) {
  const { unlockWithPassphrase, unlockWithRecoveryKey } = useEncryption();
  const { signOut } = useAuth();
  const { toast } = useToast();
  
  const [mode, setMode] = useState<"passphrase" | "recovery">("passphrase");
  const [passphrase, setPassphrase] = useState("");
  const [recoveryKey, setRecoveryKey] = useState("");
  const [showPassphrase, setShowPassphrase] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      let success = false;
      if (mode === "passphrase") {
        success = await unlockWithPassphrase(passphrase);
      } else {
        success = await unlockWithRecoveryKey(recoveryKey);
      }
      
      if (success) {
        toast({
          title: "Library Unlocked",
          description: "Your encryption key has been restored successfully."
        });
        onSuccess();
      } else {
        toast({
          title: "Unlock Failed",
          description: mode === "passphrase" ? "Incorrect passphrase." : "Invalid recovery key.",
          variant: "destructive"
        });
      }
    } catch (err) {
      toast({
        title: "Unlock Error",
        description: err instanceof Error ? err.message : "Failed to connect to verification servers.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-screen bg-background flex items-center justify-center p-4">
      <form onSubmit={handleUnlock} className="w-full max-w-md">
        <Card className="border-border shadow-xl">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl flex items-center gap-2">
              <IconLock className="text-primary" size={24} />
              Unlock Your Library
            </CardTitle>
            <CardDescription>
              {mode === "passphrase" 
                ? "Enter your passphrase to decrypt your manuscripts and resume writing." 
                : "Enter your 26-character recovery key to restore access."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {mode === "passphrase" ? (
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <Label htmlFor="unlock-passphrase">Passphrase</Label>
                    <button 
                      type="button" 
                      onClick={() => setShowPassphrase(!showPassphrase)}
                      className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                    >
                      {showPassphrase ? (
                        <>
                          <IconEyeOff size={12} /> Hide
                        </>
                      ) : (
                        <>
                          <IconEye size={12} /> Show
                        </>
                      )}
                    </button>
                  </div>
                  <button 
                    type="button" 
                    onClick={() => setMode("recovery")} 
                    className="text-xs text-primary hover:underline"
                  >
                    Use Recovery Key
                  </button>
                </div>
                <Input
                  id="unlock-passphrase"
                  type={showPassphrase ? "text" : "password"}
                  value={passphrase}
                  onChange={(e) => setPassphrase(e.target.value)}
                  placeholder="Enter E2E passphrase"
                  required
                  disabled={isSubmitting}
                />
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label htmlFor="unlock-recovery">Recovery Key</Label>
                  <button 
                    type="button" 
                    onClick={() => setMode("passphrase")} 
                    className="text-xs text-primary hover:underline"
                  >
                    Use Passphrase
                  </button>
                </div>
                <Input
                  id="unlock-recovery"
                  type="text"
                  value={recoveryKey}
                  onChange={(e) => setRecoveryKey(e.target.value)}
                  placeholder="BIRCH-RESTORE-XXXX-XXXX-XXXX"
                  className="font-mono uppercase text-center"
                  required
                  disabled={isSubmitting}
                />
              </div>
            )}
          </CardContent>
          <CardFooter className="flex gap-2">
            <Button type="button" variant="ghost" className="flex-1 text-muted-foreground" onClick={() => signOut()}>
              Sign Out
            </Button>
            <Button type="submit" className="flex-1" disabled={isSubmitting}>
              {isSubmitting ? "Decrypting..." : "Unlock"}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
}
